import { isCancel } from 'axios';
import { ref } from 'vue';
import debounce from '~/utils/debounce';
import { catchRequestError } from '~/utils/request';

export function useSearchRequest<TResponse>(
    client: unknown,
    cancelKeyName: string = 'search-request',
    debounceMs: number = 300
) {
    const loading = ref(false);
    const response = ref<TResponse>();
    const error = ref<string>();
    const cancelKey = Symbol(cancelKeyName);

    const createSearch = <TArgs extends unknown[]>(requestFn: (...args: TArgs) => Promise<TResponse>) =>
        debounce(async (...args: TArgs) => {
            loading.value = true;
            error.value = undefined;

            try {
                response.value = await requestFn(...args);

                loading.value = false;
            } catch (err) {
                if (!isCancel(err)) {
                    error.value = catchRequestError(err);
                    loading.value = false;
                }
            }
        }, debounceMs);

    const cancelSearch = () => {
        if (
            client &&
            typeof client === 'object' &&
            'cancelRequest' in client &&
            typeof client.cancelRequest === 'function'
        ) {
            client.cancelRequest(cancelKey);
        }
    };

    return {
        loading,
        response,
        error,
        createSearch,
        cancelSearch,
        cancelKey,
    };
}
